/**
 * PluginRegistrySettings — Browse and install Claude Code plugins from the marketplace.
 * Shows official Anthropic plugins with install/enable/disable controls.
 */

import { useState, useMemo } from 'react';
import {
  Puzzle, Download, Search, ExternalLink, Trash2,
  Code2, Shield, TestTube2, Sparkles, BookOpen, Rocket, Database,
  Globe, BarChart3, Eye, CheckCircle2,
} from 'lucide-react';
import { Panel, PanelHeader, PanelBody } from '../shared/Panel';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import {
  useMarketplacePlugins,
  usePlugins,
  useCreatePlugin,
  useUpdatePlugin,
  useDeletePlugin,
  type MarketplacePlugin,
  type MarketplaceSourceConfig,
  type Plugin,
} from '../../api/plugins';

// ─── Category styling ────────────────────────────────────────────

const CATEGORY_CONFIG: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
  'development': { bg: 'bg-blue-50', text: 'text-blue-600', icon: <Code2 size={10} /> },
  'productivity': { bg: 'bg-emerald-50', text: 'text-emerald-600', icon: <Rocket size={10} /> },
  'security': { bg: 'bg-amber-50', text: 'text-amber-700', icon: <Shield size={10} /> },
  'testing': { bg: 'bg-purple-50', text: 'text-purple-600', icon: <TestTube2 size={10} /> },
  'learning': { bg: 'bg-rose-50', text: 'text-rose-600', icon: <BookOpen size={10} /> },
  'database': { bg: 'bg-cyan-50', text: 'text-cyan-600', icon: <Database size={10} /> },
  'design': { bg: 'bg-pink-50', text: 'text-pink-600', icon: <Eye size={10} /> },
  'deployment': { bg: 'bg-indigo-50', text: 'text-indigo-600', icon: <Globe size={10} /> },
  'monitoring': { bg: 'bg-orange-50', text: 'text-orange-600', icon: <BarChart3 size={10} /> },
  'quality': { bg: 'bg-teal-50', text: 'text-teal-600', icon: <CheckCircle2 size={10} /> },
  'general': { bg: 'bg-surface-muted', text: 'text-muted', icon: <Sparkles size={10} /> },
};

function CategoryBadge({ category }: { category: string }) {
  const cfg = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.general;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-full uppercase tracking-wider ${cfg.bg} ${cfg.text}`}>
      {cfg.icon} {category}
    </span>
  );
}

// ─── Plugin card ─────────────────────────────────────────────────

interface PluginCardProps {
  mp: MarketplacePlugin;
  installed?: Plugin;
  sources: MarketplaceSourceConfig[];
  onInstall: (mp: MarketplacePlugin) => void;
  onToggle: (plugin: Plugin) => void;
  onUninstall: (plugin: Plugin) => void;
  installing: boolean;
}

function PluginCard({ mp, installed, sources, onInstall, onToggle, onUninstall, installing }: PluginCardProps) {
  const isInstalled = !!installed;
  const isEnabled = installed?.enabledByDefault ?? false;

  return (
    <div className={`border rounded-lg bg-white overflow-hidden transition-all ${
      isInstalled ? 'border-coral-200' : 'border-line hover:border-line-strong'
    }`}>
      <div className="flex items-start gap-3 px-4 py-3">
        {/* Icon */}
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
          isInstalled ? 'bg-coral-50' : 'bg-surface-subtle'
        }`}>
          {(CATEGORY_CONFIG[mp.category] || CATEGORY_CONFIG.general).icon
            ? <span className={isInstalled ? 'text-coral-500' : 'text-subtle'}>
                <Puzzle size={14} />
              </span>
            : <Puzzle size={14} className={isInstalled ? 'text-coral-500' : 'text-subtle'} />
          }
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13px] font-semibold text-text">{mp.name}</span>
            <CategoryBadge category={mp.category} />
            {mp.tags?.includes('community-managed') && (
              <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-500 uppercase">
                community
              </span>
            )}
            <SourceBadge marketplace={mp.marketplace} sources={sources} />
          </div>
          <p className="text-[11px] text-muted mt-0.5 line-clamp-2">{mp.description}</p>
          {mp.author && (
            <p className="text-[10px] text-subtle mt-1">by {mp.author.name}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0 mt-0.5">
          {isInstalled ? (
            <>
              <button
                onClick={() => onToggle(installed!)}
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${
                  isEnabled ? 'bg-coral-500' : 'bg-neutral-300'
                }`}
                title={isEnabled ? 'Enabled — click to disable' : 'Disabled — click to enable'}
              >
                <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                  isEnabled ? 'translate-x-4' : 'translate-x-0'
                }`} />
              </button>
              <button
                onClick={() => onUninstall(installed!)}
                className="p-1 text-subtle hover:text-red-500 hover:bg-red-50 rounded transition-all"
                title="Uninstall"
              >
                <Trash2 size={13} />
              </button>
            </>
          ) : (
            <button
              onClick={() => onInstall(mp)}
              disabled={installing}
              className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold text-coral-600 bg-coral-50 hover:bg-coral-100 rounded-lg transition-colors disabled:opacity-40"
            >
              {installing ? (
                <span className="w-3 h-3 border-2 border-coral-300 border-t-coral-600 rounded-full animate-spin" />
              ) : (
                <Download size={11} />
              )}
              Install
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Tab bar ─────────────────────────────────────────────────────

type Tab = 'browse' | 'installed';

// ─── Main component ──────────────────────────────────────────────

// ─── Source pill styling ───────────────────────────────────────────
const BUILTIN_SOURCE_STYLES: Record<string, { active: string }> = {
  'claude-plugins-official': { active: 'bg-neutral-700 text-white' },
  'claude-code': { active: 'bg-neutral-700 text-white' },
  'every-marketplace': { active: 'bg-teal-500 text-white' },
};
const DEFAULT_SOURCE_ACTIVE_STYLE = 'bg-violet-500 text-white';

function getSourcePillStyle(key: string, isActive: boolean): string {
  if (!isActive) return 'bg-surface-subtle text-muted hover:bg-surface-subtle';
  return BUILTIN_SOURCE_STYLES[key]?.active ?? DEFAULT_SOURCE_ACTIVE_STYLE;
}

// ─── Source badge in plugin card ──────────────────────────────────
function SourceBadge({ marketplace, sources }: { marketplace: string; sources: MarketplaceSourceConfig[] }) {
  const src = sources.find(s => s.key === marketplace);
  if (!src) return null;

  const style = src.isBuiltin
    ? marketplace === 'every-marketplace'
      ? 'bg-teal-50 text-teal-600'
      : marketplace === 'claude-plugins-official'
        ? 'bg-neutral-800 text-white'
        : 'bg-surface-subtle text-muted'
    : 'bg-violet-50 text-violet-500';

  const label = src.isBuiltin
    ? marketplace === 'claude-plugins-official' ? 'official'
      : marketplace === 'every-marketplace' ? 'every'
      : src.label
    : src.label;

  return (
    <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full uppercase ${style}`}>
      {label}
    </span>
  );
}

export function PluginRegistrySettings() {
  const { currentWorkspace } = useWorkspace();
  const workspaceId = currentWorkspace?.id;
  const { data: marketplaceData, isLoading: loadingMkt } = useMarketplacePlugins(workspaceId);
  const marketplace = marketplaceData?.plugins;
  const activeSources = marketplaceData?.sources ?? [];
  const { data: installed, isLoading: loadingInst } = usePlugins(workspaceId);
  const installPlugin = useCreatePlugin(workspaceId);
  const updatePlugin = useUpdatePlugin(workspaceId);
  const deletePlugin = useDeletePlugin(workspaceId);

  const [tab, setTab] = useState<Tab>('browse');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [installingSlug, setInstallingSlug] = useState<string | null>(null);

  // Build slug → installed plugin lookup
  const installedMap = useMemo(() => {
    const map = new Map<string, Plugin>();
    for (const p of installed || []) {
      map.set(p.slug, p);
    }
    return map;
  }, [installed]);

  // Get unique categories from marketplace
  const categories = useMemo(() => {
    const cats = new Set<string>();
    for (const p of marketplace || []) cats.add(p.category);
    return ['all', ...Array.from(cats).sort()];
  }, [marketplace]);

  // Filter marketplace plugins
  const filtered = useMemo(() => {
    let list = marketplace || [];
    if (tab === 'installed') {
      list = list.filter(p => installedMap.has(p.name));
    }
    if (sourceFilter !== 'all') {
      list = list.filter(p => p.marketplace === sourceFilter);
    }
    if (categoryFilter !== 'all') {
      list = list.filter(p => p.category === categoryFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        p.name.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q)
      );
    }
    return list;
  }, [marketplace, tab, categoryFilter, sourceFilter, search, installedMap]);

  const handleInstall = (mp: MarketplacePlugin) => {
    setInstallingSlug(mp.name);
    installPlugin.mutate({
      name: mp.name,
      slug: mp.name,
      description: mp.description,
      content: `Marketplace plugin: ${mp.name}@${mp.marketplace}`,
      category: mp.category,
      enabledByDefault: true,
    }, {
      onSettled: () => setInstallingSlug(null),
    });
  };

  const handleToggle = (plugin: Plugin) => {
    updatePlugin.mutate({ id: plugin.id, data: { enabledByDefault: !plugin.enabledByDefault } });
  };

  const handleUninstall = (plugin: Plugin) => {
    deletePlugin.mutate(plugin.id);
  };

  const isLoading = loadingMkt || loadingInst;
  const installedCount = installed?.length ?? 0;
  const totalCount = marketplace?.length ?? 0;

  return (
    <div className="space-y-4">
      <Panel>
        <PanelHeader
          title="Claude Code Plugins"
          subtitle={`${totalCount} available from plugin marketplaces — ${installedCount} installed`}
          action={
            <div className="flex items-center gap-1.5">
              <a
                href="https://github.com/anthropics/claude-plugins-official"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-muted hover:text-text bg-surface-subtle hover:bg-surface-subtle rounded-lg transition-colors"
              >
                <ExternalLink size={11} /> Anthropic
              </a>
              <a
                href="https://github.com/EveryInc/compound-engineering-plugin"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-teal-500 hover:text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors"
              >
                <ExternalLink size={11} /> EveryInc
              </a>
            </div>
          }
        />
        <PanelBody>
          {/* Tabs + search */}
          <div className="flex items-center gap-3 mb-3">
            <div className="flex bg-surface-subtle rounded-lg p-0.5">
              <button
                onClick={() => setTab('browse')}
                className={`px-3 py-1.5 text-[11px] font-semibold rounded-md transition-colors ${
                  tab === 'browse' ? 'bg-white text-text shadow-sm' : 'text-muted hover:text-text'
                }`}
              >
                Browse ({totalCount})
              </button>
              <button
                onClick={() => setTab('installed')}
                className={`px-3 py-1.5 text-[11px] font-semibold rounded-md transition-colors ${
                  tab === 'installed' ? 'bg-white text-text shadow-sm' : 'text-muted hover:text-text'
                }`}
              >
                Installed ({installedCount})
              </button>
            </div>

            <div className="flex-1 relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-subtle" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search plugins..."
                className="w-full pl-8 pr-3 py-1.5 border border-line rounded-lg text-[12px] text-text focus:ring-1 focus:ring-coral-500/30 focus:border-coral-400 outline-none"
              />
            </div>
          </div>

          {/* Source filter pills */}
          <div className="flex flex-wrap gap-1.5 mb-2">
            <button
              onClick={() => setSourceFilter('all')}
              className={`px-2 py-1 text-[10px] font-medium rounded-full transition-colors ${
                sourceFilter === 'all' ? 'bg-neutral-700 text-white' : 'bg-surface-subtle text-muted hover:bg-surface-subtle'
              }`}
            >
              All sources
            </button>
            {activeSources.map(src => (
              <button
                key={src.key}
                onClick={() => setSourceFilter(src.key)}
                className={`px-2 py-1 text-[10px] font-medium rounded-full transition-colors ${
                  getSourcePillStyle(src.key, sourceFilter === src.key)
                }`}
              >
                {src.label}
              </button>
            ))}
          </div>

          {/* Category pills */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setCategoryFilter(cat)}
                className={`px-2 py-1 text-[10px] font-medium rounded-full transition-colors ${
                  categoryFilter === cat
                    ? 'bg-coral-500 text-white'
                    : 'bg-surface-subtle text-muted hover:bg-surface-subtle'
                }`}
              >
                {cat === 'all' ? 'All' : cat}
              </button>
            ))}
          </div>

          {/* Plugin list */}
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-16 bg-surface-subtle rounded-lg animate-pulse" />
              ))}
            </div>
          ) : filtered.length > 0 ? (
            <div className="space-y-2">
              {filtered.map(mp => (
                <PluginCard
                  key={mp.name}
                  mp={mp}
                  installed={installedMap.get(mp.name)}
                  sources={activeSources}
                  onInstall={handleInstall}
                  onToggle={handleToggle}
                  onUninstall={handleUninstall}
                  installing={installingSlug === mp.name}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Puzzle size={24} className="text-subtle mx-auto mb-2" />
              <p className="text-[12px] text-muted font-medium">
                {tab === 'installed' ? 'No plugins installed yet' : 'No plugins match your search'}
              </p>
              {tab === 'installed' && (
                <button
                  onClick={() => setTab('browse')}
                  className="mt-2 text-[11px] font-medium text-coral-500 hover:text-coral-600"
                >
                  Browse marketplace
                </button>
              )}
            </div>
          )}
        </PanelBody>
      </Panel>
    </div>
  );
}
